import { defineStore, storeToRefs } from 'pinia';
import { api } from '@/services/http.service';
import { ElNotification } from 'element-plus';
import { createCsvDataUrl } from '@/utils/downlod-file.util';
import {
  useMgaRealMoneyBalanceListFilterStore,
} from '@/store/mga-real-money-balance/mga-real-money-balance-list-filter.store';
import { useMgaReportRealMoneyBalanceListStore } from '@/store/mga-real-money-balance/mga-real-money-balance.store';
import { clearObj, downloadFile, formatDatesInPayload } from '@marfa/bo-utils';
import { useTwoFactorStore } from '@/store/two-factor-dialog.store';
import { useTimestampStore } from '@/store/timestamp.store';
import { ApiErrorResponse } from '@/models/api/api-error.model';
import { FileDataResponse } from '@/models/app-internal/file-data-response.model';
import {
  MgaRealMoneyBalanceListFilterFields,
} from '@/models/mga-real-money-balance/mga-real-money-balance-list-filter.model';
import { ReportListCsvRequestModel } from '@/models/report/report.model';

type MgaRealMoneyBalanceListCsvStore = {
  isMgaRealMoneyBalanceListCsvLoading: boolean;
}

export const useMgaRealMoneyBalanceListCsvStore = defineStore({
  id: 'MgaRealMoneyBalanceListCsvStore',
  state: (): MgaRealMoneyBalanceListCsvStore => ({
    isMgaRealMoneyBalanceListCsvLoading: false,
  }),
  actions: {
    setMgaRealMoneyBalanceListCsvLoading(isLoading: boolean): void {
      this.isMgaRealMoneyBalanceListCsvLoading = isLoading;
    },
    getRealMoneyBalancePlayerListCsv(): Promise<FileDataResponse | void> {
      this.setMgaRealMoneyBalanceListCsvLoading(true);
      ElNotification({
        title: 'Success',
        message: 'MGA report download has been started',
        type: 'success',
      });

      const { filterFormData } = storeToRefs(useMgaRealMoneyBalanceListFilterStore());
      const { sort } = storeToRefs(useMgaReportRealMoneyBalanceListStore());
      const { twoFactorCode } = storeToRefs(useTwoFactorStore());
      const { isTimestampRequired } = storeToRefs(useTimestampStore());

      const requestPayload = {
        timestamp: isTimestampRequired.value,
        filterList: formatDatesInPayload({
          payload: { ...filterFormData.value },
          isUnixToString: false,
          isIncludeTime: true,
        }, [
          MgaRealMoneyBalanceListFilterFields.DATE_FROM,
          MgaRealMoneyBalanceListFilterFields.DATE_TO,
          MgaRealMoneyBalanceListFilterFields.EXCHANGE_RATE_DATE,
        ]),
        sort: sort.value,
        code: twoFactorCode.value,
      };

      return api.mgaRealMoneyBalance.getRealMoneyBalancePlayerListCsv.perform(requestPayload)
        .then((response) => {
          const { headers, ...data } = response;
          const csvString = Object.values(data).join('');
          const csvUrl = createCsvDataUrl(csvString);

          downloadFile(
            `mga-report-player-list-${dayjs().format('YYYY-MM-DD HH:mm:ss')}.csv`,
            csvUrl,
          );

          ElNotification({
            title: 'Success',
            message: 'MGA report has been download successfully',
            type: 'success',
          });

          return response;
        })
        .catch((error) => {
          const apiError = error as ApiErrorResponse;
          ElNotification({
            title: 'Error',
            message: apiError.response.data.message,
            type: 'error',
          });
        })
        .finally(() => {
          this.setMgaRealMoneyBalanceListCsvLoading(false);
        });
    },
  },
});
